import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ShieldCheck, UserPlus, CalendarClock, Scale, ChevronRight,
  History, GitCompare, Loader2, Info, ArrowRight, Lock,
  X, CheckCircle2, AlertTriangle, Sparkles, Minus, Plus,
} from "lucide-react";

const KIND_META = {
  coverage:   { label: "Coverage", Icon: ShieldCheck,   subtab: "coverage_sim" },
  assignment: { label: "Assignment", Icon: UserPlus,    subtab: null /* embedded in coverage tab */ },
  leave:      { label: "Leave",    Icon: CalendarClock, subtab: "leave_sim" },
  fairness:   { label: "Fairness", Icon: Scale,         subtab: "fairness_sim" },
};

const OUTCOME_META = {
  improved:           { label: "Improved",         bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", Icon: CheckCircle2 },
  attention_needed:   { label: "Attention needed", bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-700",   Icon: AlertTriangle },
  no_change:          { label: "No change",        bg: "bg-slate-50",   border: "border-slate-200",   text: "text-slate-600",   Icon: Minus },
  new:                { label: "New",              bg: "bg-[#F0F6FF]",  border: "border-[#BFD3FB]",   text: "text-[#1D4ED8]",   Icon: Plus },
  removed:            { label: "Removed",          bg: "bg-slate-50",   border: "border-slate-200 border-dashed", text: "text-slate-500", Icon: X },
};

function relTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso); const now = new Date();
  const mins = Math.round((now - d) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

export default function SimulationHub({ onNavigateToKind }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareKind, setCompareKind] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/simulations-hub-summary");
        setData(data);
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || "Failed to load hub");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="py-8 text-center text-sm text-[#4B5773]"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading hub…</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="simulation-hub">
      <div className="rounded-lg border border-[#BFD3FB] bg-[#F0F6FF] p-3 flex gap-3" data-testid="hub-intro">
        <Info className="h-4 w-4 text-[#1D4ED8] mt-0.5 flex-shrink-0" />
        <div className="text-xs text-[#1D4ED8] leading-relaxed">
          <span className="font-medium">Simulation hub.</span> One place to see how Coverage, Assignment, Leave and Fairness are looking across your team. All four are read-only insights, nothing here changes your rota, leave or assignments. Use <strong>Compare</strong> to see how the latest check differs from the previous one.
        </div>
      </div>

      {/* Status cards FIRST (Adjustment 2) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hub-tiles">
        {data.tiles.map((t) => <HubTile key={t.kind} tile={t}
          onOpen={() => onNavigateToKind?.(KIND_META[t.kind]?.subtab || "coverage_sim")}
          onCompare={() => setCompareKind(t.kind)} />)}
      </div>

      {/* Activity timeline SECOND */}
      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="hub-activity">
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-[#0B1A36]" />
          <h3 className="font-medium text-sm text-[#0B1A36]">Recent activity</h3>
        </div>
        {data.activity.length === 0 ? (
          <p className="text-xs text-[#4B5773]">No simulation runs yet.</p>
        ) : (
          <ol className="relative border-l border-slate-200 pl-5 space-y-3">
            {data.activity.map((a) => {
              const meta = KIND_META[a.kind] || {};
              const Icon = meta.Icon || History;
              return (
                <li key={a.simulation_id} className="relative" data-testid={`hub-activity-${a.simulation_id}`}>
                  <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#2563EB] ring-4 ring-[#DBE7FE]" />
                  <div className="text-[11px] uppercase tracking-widest text-[#64748B]">
                    {new Date(a.created_at).toLocaleString("en-GB", {day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit", hour12:false})}
                  </div>
                  <div className="text-sm text-[#0B1A36] mt-0.5">
                    <Icon className="h-3.5 w-3.5 inline mr-1 text-[#2563EB]" />
                    {meta.label} check by {a.actor_name}, {a.headline?.primary || "-"}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Live L3, Drafts awaiting review */}
      <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/60 p-4" data-testid="hub-drafts-card">        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-amber-700" />
          <h3 className="font-medium text-sm text-[#0B1A36]">Drafts awaiting review</h3>
          <span className="text-[10px] uppercase tracking-widest text-amber-800 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200 ml-auto">
            Draft · Not live
          </span>
        </div>
        {data.drafts_awaiting_review > 0 ? (
          <>
            <div className="text-2xl font-medium text-[#0B1A36] mt-2" data-testid="hub-drafts-count">
              {data.drafts_awaiting_review}
            </div>
            <p className="text-xs text-[#4B5773] mt-0.5">
              {data.drafts_awaiting_review === 1 ? "Draft is" : "Drafts are"} waiting for manager approval. Drafts never touch live data until you click Apply.
            </p>
            {data.drafts_awaiting_breakdown && (
              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-amber-900"
                   data-testid="hub-drafts-breakdown">
                <span className="px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200"
                      data-testid="hub-drafts-breakdown-assignment">
                  {data.drafts_awaiting_breakdown.assignment || 0} assignment{(data.drafts_awaiting_breakdown.assignment || 0) === 1 ? "" : "s"}
                </span>
                <span className="px-1.5 py-0.5 rounded bg-amber-100 border border-amber-200"
                      data-testid="hub-drafts-breakdown-leave">
                  {data.drafts_awaiting_breakdown.leave_decision || 0} leave decision{(data.drafts_awaiting_breakdown.leave_decision || 0) === 1 ? "" : "s"}
                </span>
              </div>
            )}
            <div className="mt-3 flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
                      onClick={() => navigate("/app/drafts?kind=assignment")}
                      data-testid="hub-drafts-open">
                Review assignment drafts <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
              {(data.drafts_awaiting_breakdown?.leave_decision || 0) > 0 && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
                        onClick={() => navigate("/app/drafts?kind=leave_decision")}
                        data-testid="hub-drafts-open-leave">
                  Review leave drafts <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </>
        ) : (
          <p className="text-xs text-[#4B5773] mt-1">
            No drafts pending review. When coverage gaps appear with a strong-fit candidate, or pending leave requests look clean, NextRota can propose drafts here. Drafts never touch live data until you click Apply.
          </p>
        )}
      </div>

      {/* Historical Performance () */}
      <HubHistoricalPerformance navigate={navigate} />

      {/* Auto Apply summary () */}
      <HubAutoApplySummary navigate={navigate} />

      {/* Automation Readiness () */}
      <HubAutomationReadiness navigate={navigate} />

      {/* Future sections marker, invisible to users, kept for layout reservation */}
      <div className="hidden" data-testid="hub-future-sections">
        {(data.future_sections || []).join(",")}
      </div>

      {compareKind && (
        <CompareDrawer kind={compareKind} onClose={() => setCompareKind(null)} />
      )}
    </div>
  );
}

function HubTile({ tile, onOpen, onCompare }) {
  const meta = KIND_META[tile.kind] || {};
  const Icon = meta.Icon || ShieldCheck;
  const ready = !!tile.latest_simulation_id;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col"
         data-testid={`hub-tile-${tile.kind}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-[#2563EB]" />
        <span className="text-sm font-medium text-[#0B1A36]">{meta.label}</span>
        {tile.engine_version && (
          <span className="text-[10px] uppercase tracking-widest text-[#1D4ED8] px-1.5 py-0.5 rounded bg-[#DBE7FE] border border-[#BFD3FB] ml-auto">
            {tile.engine_version}
          </span>
        )}
      </div>
      {ready ? (
        <>
          <div className="text-base font-medium text-[#0B1A36]" data-testid={`hub-tile-headline-${tile.kind}`}>
            {tile.headline?.primary}
          </div>
          <div className="text-xs text-[#4B5773] mt-0.5">{tile.headline?.secondary}</div>
          <div className="text-[11px] text-[#64748B] mt-2">Last run {relTime(tile.latest_created_at)}</div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onOpen} data-testid={`hub-tile-open-${tile.kind}`}>
              View <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onCompare} data-testid={`hub-tile-compare-${tile.kind}`}>
              <GitCompare className="h-3 w-3 mr-1" /> Compare
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="text-sm text-[#4B5773]">No run yet for this kind.</div>
          <Button size="sm" variant="outline" className="h-7 text-xs mt-3" onClick={onOpen} data-testid={`hub-tile-open-${tile.kind}`}>
            Open <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </>
      )}
    </div>
  );
}

function CompareDrawer({ kind, onClose }) {
  const [runs, setRuns] = useState([]);
  const [leftId, setLeftId] = useState(null);
  const [rightId, setRightId] = useState(null);
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Fetch recent runs of this kind
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/simulations?kind=${kind}&limit=20`);
        const items = data?.items || [];
        setRuns(items);
        if (items.length >= 2) {
          setRightId(items[0].simulation_id);
          setLeftId(items[1].simulation_id);
        }
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || "Failed to load history");
      } finally { setLoading(false); }
    })();
  }, [kind]);

  // Compute diff when both IDs chosen
  useEffect(() => {
    if (!leftId || !rightId || leftId === rightId) { setDiff(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/simulations-compare", {
          left_simulation_id: leftId, right_simulation_id: rightId,
        });
        if (!cancelled) setDiff(data);
      } catch (err) {
        if (!cancelled) toast.error(formatError(err.response?.data?.detail) || "Failed to compare");
      }
    })();
    return () => { cancelled = true; };
  }, [leftId, rightId]);

  const rows = (diff?.row_diffs || []);
  const visibleRows = showAll ? rows : rows.slice(0, 20);
  const KindIcon = KIND_META[kind]?.Icon || ShieldCheck;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="compare-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full sm:w-[720px] bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <KindIcon className="h-5 w-5 text-[#2563EB]" />
          <h3 className="font-medium text-base text-[#0B1A36]">Compare {KIND_META[kind]?.label} runs</h3>
          <span className="text-[10px] uppercase tracking-widest text-[#64748B] ml-2 inline-flex items-center gap-1">
            <Lock className="h-3 w-3" /> Read-only
          </span>
          <button type="button" className="ml-auto text-slate-500 hover:text-slate-700"
                  onClick={onClose} data-testid="compare-drawer-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
          {loading ? (
            <div className="text-xs text-[#4B5773]"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" />Loading runs…</div>
          ) : runs.length < 2 ? (
            <div className="text-xs text-[#4B5773]">At least two runs of this kind are needed to compare. Run another check first.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <RunPicker label="Previous (left)" value={leftId} onChange={setLeftId} runs={runs} testid="compare-left" />
              <RunPicker label="Latest (right)" value={rightId} onChange={setRightId} runs={runs} testid="compare-right" />
            </div>
          )}
        </div>

        {diff && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="compare-content">
            {diff.headline_deltas?.length > 0 && (
              <div>
                <h4 className="text-[11px] uppercase tracking-widest text-[#64748B] mb-2">Headline changes</h4>
                <div className="grid grid-cols-3 gap-2">
                  {diff.headline_deltas.map((h) => {
                    const m = OUTCOME_META[h.tone] || OUTCOME_META.no_change;
                    return (
                      <div key={h.metric} className={`rounded border ${m.border} ${m.bg} px-3 py-2`}>
                        <div className="text-[11px] text-[#64748B]">{h.metric.replaceAll("_", " ")}</div>
                        <div className={`text-sm font-medium ${m.text}`}>
                          {h.left} → {h.right} <span className="text-[10px]">({h.delta > 0 ? "+" : ""}{h.delta})</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-[11px] uppercase tracking-widest text-[#64748B] mb-2">
                Row-by-row changes ({rows.length})
              </h4>
              {rows.length === 0 ? (
                <p className="text-xs text-[#4B5773]">Nothing changed between these runs.</p>
              ) : (
                <ul className="space-y-2" data-testid="compare-rows">
                  {visibleRows.map((r) => <DiffRow key={r.signature + r.outcome} row={r} />)}
                </ul>
              )}
              {rows.length > 20 && (
                <button type="button"
                        onClick={() => setShowAll((s) => !s)}
                        className="text-xs text-[#1D4ED8] hover:underline mt-3"
                        data-testid="compare-show-all">
                  {showAll ? "Show top 20 only" : `Show all ${rows.length} changes`}
                </button>
              )}
            </div>

            {diff.limitations?.length > 0 && (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[11px] text-[#475569]">
                <div className="font-medium text-[#0B1A36] mb-1">About this view</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {diff.limitations.map((l) => <li key={l}>{l}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RunPicker({ label, value, onChange, runs, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-[#64748B]">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white"
              data-testid={testid}>
        {runs.map((r) => (
          <option key={r.simulation_id} value={r.simulation_id}>
            {new Date(r.created_at).toLocaleString("en-GB", {day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit", hour12:false})}
            {", "}{r.simulation_id.slice(-6)}
          </option>
        ))}
      </select>
    </div>
  );
}

function DiffRow({ row }) {
  const m = OUTCOME_META[row.outcome] || OUTCOME_META.no_change;
  const Icon = m.Icon;
  return (
    <li className={`rounded border ${m.border} ${m.bg} px-3 py-2`}
        data-testid={`compare-row-${row.signature}`}>
      <div className="flex items-start gap-2">
        <Icon className={`h-3.5 w-3.5 mt-0.5 ${m.text} flex-shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium text-[#0B1A36]">{row.label}</span>
            <span className={`text-[10px] uppercase tracking-widest ${m.text}`}>{m.label}</span>
          </div>
          {/* Adjustment 4, explanation is the main content, badge is corner-pill */}
          {row.why_text && (
            <p className="text-sm text-[#0B1A36] mt-1" data-testid={`compare-why-${row.signature}`}>
              {row.why_text}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

function HubHistoricalPerformance({ navigate }) {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/replays?limit=3");
        setLatest(data?.items || []);
      } catch { setLatest([]); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="hub-historical-performance">
      <div className="flex items-center gap-2">
        <Lock className="h-4 w-4 text-blue-700" />
        <h3 className="font-medium text-sm text-[#0B1A36]">Historical Performance</h3>
        <span className="text-[10px] uppercase tracking-widest text-blue-800 px-1.5 py-0.5 rounded bg-blue-100 border border-blue-200 ml-auto">
          Read-only · No live changes
        </span>
      </div>
      {!latest || latest.length === 0 ? (
        <p className="text-xs text-[#4B5773] mt-1">
          See what NextRota would have done in past periods. Useful for building trust in the engine before letting it drive more decisions.
        </p>
      ) : (
        <>
          <div className="mt-2" data-testid="hub-historical-trust">
            <div className="text-base text-[#0B1A36]">
              <strong>{latest[0]?.result_summary?.total_classified ?? 0}</strong> decisions analysed
              {(latest[0]?.result_summary?.engine_would_have_resolved_count ?? 0) > 0 && (
                <> · <strong>{latest[0].result_summary.engine_would_have_resolved_count}</strong> recommendations validated</>
              )}
            </div>
            <div className="text-xs text-[#4B5773] mt-1">
              Trust score <strong>{latest[0]?.result_summary?.trust_score_pct ?? 0}%</strong>
              {", "}interpret alongside the totals on the right.
            </div>
          </div>
          <p className="text-xs text-[#4B5773] mt-1">
            Window: {latest[0]?.scope?.from} → {latest[0]?.scope?.to}.
            {latest.length > 1 && ` ${latest.length} runs in your history.`}
          </p>
        </>
      )}
      <div className="mt-3">
        <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-900 hover:bg-blue-50"
                onClick={() => navigate("/app/replays")}
                data-testid="hub-historical-open">
          View Historical Performance <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function HubAutomationReadiness({ navigate }) {
  const [data, setData] = useState(null);
  const [cem, setCem] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [r, c] = await Promise.all([
          api.get("/automation/blockers"),
          api.get("/automation/cem/status").catch(() => ({ data: null })),
        ]);
        setData(r.data);
        setCem(c.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading || !data) return null;
  const journey = data.journey || {};
  const cemPaused = !!cem?.cem_paused;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="hub-automation-readiness">
      <div className="flex items-center gap-2 flex-wrap">
        <Lock className="h-4 w-4 text-blue-700" />
        <h3 className="font-medium text-sm text-[#0B1A36]">Automation Readiness</h3>
        {cemPaused && (
          <span className="text-[10px] uppercase tracking-widest text-rose-800 px-1.5 py-0.5 rounded bg-rose-100 border border-rose-200"
                data-testid="hub-cem-paused">
            Auto-paused
          </span>
        )}
        <span className="text-[10px] uppercase tracking-widest text-blue-800 px-1.5 py-0.5 rounded bg-blue-100 border border-blue-200 ml-auto">
          Read-only · No live changes
        </span>
      </div>
      {cemPaused && (
        <p className="text-xs text-rose-800 mt-1" data-testid="hub-cem-paused-line">
          NextRota paused some automation after a recent setup change. Open Readiness to review and restore.
        </p>
      )}
      <div className="mt-2 text-base text-[#0B1A36]">
        <strong>{data.critical_blocker_count}</strong> blocker{data.critical_blocker_count === 1 ? "" : "s"} preventing higher automation
      </div>
      <div className="text-xs text-[#4B5773] mt-1">
        Readiness score <strong>{data.score}/100</strong>
        {journey.next_level_label && (
          <> · journey to <strong>{String(journey.next_level_label).replace(/^L[0-5]\s+/, "")}</strong> {journey.requirements_met}/{journey.requirements_total}</>
        )}
      </div>
      {(journey.top_blockers || []).length > 0 && (
        <ul className="text-[11px] text-[#4B5773] mt-1 space-y-0.5">
          {journey.top_blockers.slice(0, 3).map((b, i) => (
            <li key={i}>· {b.label}</li>
          ))}
        </ul>
      )}
      <div className="mt-3">
        <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-900 hover:bg-blue-50"
                onClick={() => navigate("/app/readiness")}
                data-testid="hub-readiness-open">
          View Automation Readiness <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}


function HubAutoApplySummary({ navigate }) {
  // read-only Auto Apply summary tile. Only renders when the
  // tenant is at L4 to avoid clutter for L1/L2/L3 tenants.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/automation/auto-apply/status");
        setData(r.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading || !data || data.current_level !== "L4") return null;
  const enabled = !!data.action_enabled;
  const remaining = data.remaining_today ?? 0;
  const applied = data.applied_today ?? 0;
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4"
         data-testid="hub-auto-apply-summary">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="h-4 w-4 text-blue-700" />
        <h3 className="font-medium text-sm text-[#0B1A36]">Auto-applied assignments</h3>
        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ml-auto ${
          enabled
            ? "text-blue-900 bg-blue-100 border-blue-200"
            : "text-slate-600 bg-slate-100 border-slate-200"}`}>
          {enabled ? "Enabled" : "Off"}
        </span>
      </div>
      {enabled ? (
        <>
          <div className="text-2xl font-medium text-[#0B1A36] mt-2"
               data-testid="hub-auto-apply-applied-today">
            {applied}<span className="text-base text-[#4B5773]"> / {data.daily_cap} today</span>
          </div>
          <p className="text-xs text-[#4B5773] mt-0.5">
            Auto-applied today. {remaining} remaining in today&apos;s budget.
            Only strong-fit assignment recommendations qualify, every safety
            check is re-run at apply time.
          </p>
          {data.circuit_breaker?.consecutive_failures > 0 && (
            <p className="text-xs text-rose-700 mt-1">
              Circuit breaker: {data.circuit_breaker.consecutive_failures} of {data.circuit_breaker.limit} consecutive failures recorded.
            </p>
          )}
        </>
      ) : (
        <p className="text-xs text-[#4B5773] mt-1">
          Auto-apply is currently off. Turn it on from the Automation Settings
          tab to start applying strong-fit recommendations automatically. Off
          by default for safety.
        </p>
      )}
      <div className="mt-3">
        <Button size="sm" variant="outline"
                className="h-7 text-xs border-blue-300 text-blue-900 hover:bg-blue-50"
                onClick={() => navigate("/app/automation#advanced")}
                data-testid="hub-auto-apply-open">
          Open automation settings <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}

